<template>
    <form-modal
        title="Create Audit"
        v-model="isShowCreateEditor"
        @submit="createAudit"
        :scrollable="false"
        :loading="loading">
        <fieldset>
            <div class="mb-3 row">
                <label
                    for="auditorName"
                    class="col-sm-3 col-form-label"
                    >Audit Name:</label
                >
                <div class="col-sm-9">
                    <b-input
                        name="auditorName"
                        type="text"
                        v-model="audit.auditName"
                        :error="errors.auditName" />
                </div>
            </div>
            <div class="mb-3 row">
                <label
                    for="disabledSelect"
                    class="col-sm-3 col-form-label"
                    >Agency:</label
                >
                <div class="col-sm-9">
                    <Multiselect
                        v-model="audit.agency"
                        :options="agencies"
                        :multiple="false"
                        :close-on-select="true"
                        :class="{'is-invalid': !!errors.agency}"
                        :label="'label'"
                        :searchable="true"
                        :filter-results="true"
                        :strict="false"
                        :submit="false"
                        :native-support="true" />
                    <span
                        class="invalid-feedback"
                        role="alert"
                        v-if="errors.agency">
                        {{ errors.agency }}
                    </span>
                </div>
            </div>

            <div class="mb-3 row">
                <label class="col-sm-3 col-form-label">Period Under Review:</label>
                <div class="col-sm-9">
                    <div class="row">
                        <div class="col">
                            <label
                                for="startMonth"
                                class="col-form-label"
                                >Start Month:</label
                            >

                            <b-date-picker
                                name="startMonth"
                                :error="errors.startMonth"
                                :month-picker="true"
                                v-model="startMonth"></b-date-picker>
                        </div>
                        <div class="col">
                            <label
                                for="endMonth"
                                class="col-form-label"
                                >End Month:</label
                            >
                            <b-date-picker
                                name="endMonth"
                                :error="errors.endMonth"
                                :month-picker="true"
                                v-model="endMonth"></b-date-picker>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-3 row">
                <label class="col-sm-3 col-form-label">Audit to Take Place</label>
                <div class="col-sm-9">
                    <div class="row">
                        <div class="col">
                            <label
                                for="startDate"
                                class="col-form-label"
                                >Start Date:</label
                            >
                            <b-date-picker
                                name="startDate"
                                :error="errors.startDate"
                                v-model="audit.startDate"></b-date-picker>
                        </div>
                        <div class="col">
                            <label
                                for="plannedEndDate"
                                class="col-form-label"
                                >End Date:</label
                            >

                            <b-date-picker
                                name="plannedEndDate"
                                :error="errors.plannedEndDate"
                                v-model="audit.plannedEndDate"></b-date-picker>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-4 row">
                <label
                    for="agencyContactRole"
                    class="col-sm-3 col-form-label"
                    >Agency Contact:</label
                >
                <div class="col-sm-9">
                    <select
                        id="agencyContactRole"
                        v-model="audit.agencyContactRole"
                        class="form-select">
                        <option value="0">N/A</option>
                        <option
                            v-for="item in agencyContactsStore.items"
                            :value="item.value"
                            :key="item.value">
                            {{ item.label }}
                        </option>
                    </select>
                    <span
                        class="invalid-feedback"
                        role="alert"
                        v-if="errors.agencyContactRole">
                        {{ errors.agencyContactRole }}
                    </span>
                </div>
            </div>
            <div class="mb-3 row">
                <label
                    for="auditorName"
                    class="col-sm-3 col-form-label"
                    >Auditor Name:</label
                >
                <div class="col-sm-9">
                    <b-input
                        name="auditorName"
                        type="text"
                        v-model="audit.auditorName"
                        :error="errors.auditorName" />
                </div>
            </div>
            <div class="mb-3 row">
                <label
                    for="auditorEmailAddress"
                    class="col-sm-3 col-form-label"
                    >Auditor Email Address:</label
                >
                <div class="col-sm-9">
                    <b-input
                        name="auditorEmailAddress"
                        type="text"
                        v-model="audit.auditorEmailAddress"
                        :error="errors.auditorEmailAddress" />
                </div>
            </div>
        </fieldset>
    </form-modal>
    <system-alerts></system-alerts>
    <div class="audits-grid">
        <b-grid
            caption="List of audits"
            :tableOptions="auditStore.tableOptions"
            :filterableFields="auditStore.filterableFields"
            :filterOptions="auditStore.filterOptions"
            @optionChange="getData"
            :items="auditStore.items"
            :columns="columns"
            :loading="loading">
            <template #beforeFilter>
                <button
                    @click.prevent="showCreateEditor"
                    v-if="canAdd"
                    class="btn btn-primary">
                    Create Audit
                </button>
            </template>

            <template #cell.auditName="{item}">
                <a
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    v-if="item.status == 'In Progress' || item.status == 'Closed' || item.status == 'New'"
                    :href="`AgencyCommunicationAuditsDetails.aspx?id=${item.id}`">
                    {{ item["auditName"] }}</a
                >
                <span v-else>
                    {{ item["auditName"] }}
                </span>
            </template>
            <template #header.bottom.documentsAccepted> (% of requested) </template>
            <template #cell.documentsAccepted="{item}">
                <span v-if="item.documentsAccepted">
                    {{ item.documentsAccepted }} ({{ item.documentsAcceptedPercentage }}%)
                </span>
            </template>
            <template #header.bottom.uploadedNarratives> (% of incidents) </template>
            <template #cell.uploadedNarratives="{item}">
                <span v-if="item.uploadedNarratives">
                    {{ item.uploadedNarratives }} ({{ item.uploadedNarrativesPercentage }}%)
                </span>
            </template>

            <template #header.bottom.completedReviews> (% of incidents) </template>
            <template #cell.completedReviews="{item}">
                <span v-if="item.completedReviews">
                    {{ item.completedReviews }} ({{ item.completedReviewsPercentage }}%)
                </span>
            </template>

            <template #header.bottom.passedReviews> (% of completed reviews) </template>

            <template #cell.passedReviews="{item}">
                <span v-if="item.passedReviews"> {{ item.passedReviews }} ({{ item.passedReviewsPercentage }}%) </span>
            </template>

            <template #cell.status="{item}">
                <a
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    v-if="item.status == 'Failed'"
                    :href="`AgencyCommunicationAuditsDetails.aspx?id=${item.id}`">
                    {{ item["status"] }}</a
                >
                <span v-else>
                    {{ item["status"] }}
                </span>
            </template>

            <template #cell.reportFileId="{item}">
                <a
                    :href="`api/v1/audit/file/${item.reportFileId}`"
                    class="btn btn-sm btn-secondary"
                    v-if="item.reportFileId"
                    >View Report</a
                >
                <span v-else></span>
            </template>
        </b-grid>
    </div>
</template>
<script lang="ts" setup>
import {ref, reactive, computed, watch} from "vue";
import {BInput, BDatePicker} from "./Fields";
import BGrid from "./BGrid/BGrid.vue";
import {BTableColumn} from "./BTable/BTable.types";
import Multiselect from "@vueform/multiselect";
import {useOriStore} from "@/stores/oriStore";
import {SelectItem, UploadType} from "@/types";
import {useAuditStore} from "@/stores/auditStore";
import type {Audit} from "../audit.types";
import {useFormValidation} from "../validation";
import {object, string, date} from "yup";
import {useAgencyContactsStore} from "@/stores/agencyContactsStore";
import {debounce} from "vue-debounce";
import {convertToUTC} from "@/utils";
import {FormModal, InformationModal, SystemAlerts} from "./Misc";
import {storeToRefs} from "pinia";

const props = defineProps<{
    title: String;
    runAudits: boolean;
    createAudits: boolean;
}>();

var updateData = debounce(() => getData(), "15000ms");

const audit = ref<Audit>({} as Audit);

const isShowCreateEditor = ref(false);

const startMonth = ref();
const endMonth = ref();

watch(startMonth, (newStartMonth) => {
    if (newStartMonth) {
        audit.value!.startMonth = convertToUTC(new Date(newStartMonth.year, newStartMonth.month, 1));
    }
});

watch(endMonth, (newEndMonth) => {
    if (newEndMonth) {
        audit.value!.endMonth = convertToUTC(new Date(newEndMonth.year, newEndMonth.month + 1, 0));
    }
});

const auditStore = useAuditStore();
const {loading} = storeToRefs(auditStore);
const agencyContactsStore = useAgencyContactsStore();

const schema = object<Audit>({
    auditName: string().required().label("Audit Name"),
    agency: string().required().label("Agency"),
    startDate: date().required().label("Start Date"),
    plannedEndDate: date()
        .required()
        .label("End Date")
        .when("startDate", (startDate, schema) => {
            if (audit.value.startDate) {
                const dayAfter = new Date(new Date(audit.value!.startDate).getTime() + 86400000);
                return schema.min(dayAfter, "End date cannot be earlier than start date");
            }
            return schema;
        }),
    startMonth: date().required().label("Start Month"),
    agencyContactRole: string().required().label("Agency Contact"),
    endMonth: date()
        .required()
        .label("End Month")
        .when("plannedEndDate", (plannedEndDate, schema) => {
            if (audit.value.plannedEndDate) {
                var endDate = new Date(audit.value!.plannedEndDate);
                const dayAfter = new Date(endDate.getFullYear(), endDate.getUTCMonth() + 1, 1);
                return schema.max(dayAfter, "End month cannot be later than when the Audit is taking place");
            }
            return schema;
        })
        .when("startMonth", (startMonth, schema) => {
            if (audit.value.startMonth) {
                const dayAfter = new Date(audit.value.startMonth.getTime() + 86400000);
                return schema.min(dayAfter, "End month has to be no earlier than start month");
            }
            return schema;
        }),
    auditorName: string().required().label("Auditor Name"),
    auditorEmailAddress: string().email().label("Auditor Email Address"),
});

const oriStore = useOriStore();
oriStore.dataset = UploadType.nibrs;

const agencies = computed(() => {
    const values = oriStore.oris;
    let list = values.map(
        (m) =>
            ({
                label: `${m.ori} - ${m.agency}`,
                value: m.ori,
            } as SelectItem)
    );

    return list;
});

const columns = reactive<BTableColumn[]>([
    {
        key: "auditName",
        text: "Audit Name",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["auditName"],
    },
    {
        key: "agency",
        text: "Agency Name",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["agencyName"],
    },
    {
        key: "startDate",
        text: "Start Date",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item.startDate,
    },
    {
        key: "status",
        text: "Status",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item["status"],
    },

    {
        key: "incidents",
        text: "Documents requested",
        sortable: false,
        class: (item) => ["text-center"],
        columnClass: ["text-center align-top"],
        value: (item) => item["documents"],
    },
    {
        key: "documentsAccepted",
        text: "Documents accepted",
        sortable: false,
        class: (item) => ["text-center"],
        columnClass: ["text-center align-top"],
        value: (item) => item["documentsAccepted"],
    },

    {
        key: "incidents",
        text: "Incidents",
        sortable: false,
        class: (item) => ["text-center"],
        columnClass: ["text-center align-top"],
        value: (item) => item["incidents"],
    },
    {
        key: "uploadedNarratives",
        text: "Uploaded Narratives",
        class: (item) => ["text-center"],
        columnClass: ["text-center"],
        sortable: false,
        value: (item) => item["uploadedNarratives"],
    },
    {
        key: "completedReviews",
        text: "Completed Reviews",
        columnClass: ["text-center"],
        class: (item) => ["text-center"],
        sortable: false,
        value: (item) => item["completedReviews"],
    },
    {
        key: "passedReviews",
        text: "Passed Reviews",
        class: (item) => ["text-center"],
        columnClass: ["text-center"],
        sortable: false,
        value: (item) => item["passedReviews"],
    },
    {
        key: "reportFileId",
        text: "Report File",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-wrap"],
        value: (item) => item["reportFileId"],
    },
]);

const showCreateEditor = async function () {
    audit.value = {} as Audit;
    resetValidation();
    isShowCreateEditor.value = true;
    let user = await auditStore.getCurrentUser();
    audit.value.auditorEmailAddress = user.email;
    audit.value.agencyContactRole = "0";
    audit.value.auditorName = `${user.firstName} ${user.lastName}`.trim();
    startMonth.value = "";
    endMonth.value = "";
};

const getData = async function () {
    await auditStore.list();
    await updateData();
};

const canAdd = computed(() => props.createAudits);

const canDelete = function (): boolean {
    return props.runAudits;
};

const {handleSubmit, errors, resetValidation} = useFormValidation();

const createAudit = async () => {
    if (await handleSubmit(schema, audit.value)) {
        await auditStore.create(audit.value);
        await getData();
        isShowCreateEditor.value = false;
        audit.value = {} as Audit;
    }
};

await getData();
await agencyContactsStore.list();
</script>
